import api from "../../../../services/axios";

export interface TenantDto {
  id: string;
  name: string;
  code: string;
  email: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country: string;
  postalCode?: string;
  logoUrl?: string;
  board?: string;
  timeZoneId?: string;
  academicYearId?: string;
  planTier: string;
  subscriptionStartAt?: string;
  subscriptionEndAt?: string;
  isTrial: boolean;
  trialEndsAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateTenantRequest {
  name: string;
  code: string;
  email: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  /**
   * ISO 3166-1 alpha-2 code of the school's country: any ISO country. It sets the default currency and time zone, and
   * is the default region for the administrator's national phone number, so it must be set when the tenant is created.
   */
  countryCode?: string;
  /** IANA time zone; omit to take the country's first zone. Needed to pick among a multi-zone country's zones. */
  timeZoneId?: string;
  postalCode?: string;
  schoolAdminEmail: string;
  schoolAdminFirstName: string;
  schoolAdminLastName: string;
  schoolAdminPhoneNumber: string;
  gstNumber?: string;
  panNumber?: string;
  logoUrl?: string;
  board?: string;
  planTier: string;
}

export interface UpdateTenantRequest {
  name: string;
  email: string;
  phoneNumber?: string;
  board?: string;
  logoUrl?: string;
}

type ApiError = {
  code?: string | null;
  message?: string | null;
};

type ApiResponse<T> = {
  data?: T;
  meta?: { statusCode?: number; message?: string };
  errors?: ApiError[] | null;
};

function unwrapApiResponse<T>(payload: ApiResponse<T>): T {
  const firstError = payload?.errors?.[0]?.message;
  if (firstError) {
    throw new Error(firstError);
  }

  if (payload?.data === undefined) {
    throw new Error(payload?.meta?.message || "Unexpected API response");
  }

  return payload.data;
}

export async function getTenants(): Promise<TenantDto[]> {
  const resp = await api.get<ApiResponse<TenantDto[]>>("/user-access/tenants/all");
  return unwrapApiResponse(resp.data);
}

export async function getTenant(tenantId: string): Promise<TenantDto> {
  const resp = await api.get<ApiResponse<TenantDto>>(`/user-access/tenants/${tenantId}`);
  return unwrapApiResponse(resp.data);
}

export async function createTenant(input: CreateTenantRequest): Promise<string> {
  const resp = await api.post<ApiResponse<string>>("/user-access/tenants", input);
  return unwrapApiResponse(resp.data);
}

export async function updateTenant(
  tenantId: string,
  input: UpdateTenantRequest,
): Promise<string> {
  const resp = await api.put<ApiResponse<string>>(
    `/user-access/tenants/${tenantId}`,
    input,
  );
  return unwrapApiResponse(resp.data);
}

export async function setTenantTimeZone(
  tenantId: string,
  timeZoneId: string,
): Promise<string> {
  const resp = await api.put<ApiResponse<string>>(
    `/user-access/tenants/${tenantId}/time-zone`,
    { timeZoneId },
  );
  return unwrapApiResponse(resp.data);
}

export async function deactivateTenant(tenantId: string): Promise<string> {
  const resp = await api.post<ApiResponse<string>>(
    `/user-access/tenants/${tenantId}/deactivate`,
  );
  return unwrapApiResponse(resp.data);
}

export async function reactivateTenant(tenantId: string): Promise<string> {
  const resp = await api.post<ApiResponse<string>>(
    `/user-access/tenants/${tenantId}/reactivate`,
  );
  return unwrapApiResponse(resp.data);
}
